/**
 * Utility for formatting E.164 phone numbers for display purposes
 * Converts international E.164 format to user-friendly display formats
 */

/**
 * Country-specific formatting patterns
 */
const COUNTRY_FORMATS = {
  // North American Numbering Plan (US, Canada, etc.)
  1: {
    pattern: /^(\d{3})(\d{3})(\d{4})$/,
    format: "($1) $2-$3",
    displayName: "US/Canada",
  },
  // United Kingdom
  44: {
    pattern: /^(\d{2})(\d{4})(\d{4})$/,
    format: "$1 $2 $3",
    displayName: "United Kingdom",
  },
  // France
  33: {
    pattern: /^(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})$/,
    format: "$1 $2 $3 $4 $5",
    displayName: "France",
  },
  // Germany
  49: {
    pattern: /^(\d{3})(\d{3})(\d{5})$/,
    format: "$1 $2 $3",
    displayName: "Germany",
  },
  // Italy
  39: {
    pattern: /^(\d{3})(\d{3})(\d{4})$/,
    format: "$1 $2 $3",
    displayName: "Italy",
  },
  // Spain
  34: {
    pattern: /^(\d{3})(\d{3})(\d{3})$/,
    format: "$1 $2 $3",
    displayName: "Spain",
  },
  // Australia
  61: {
    pattern: /^(\d{3})(\d{3})(\d{3})$/,
    format: "$1 $2 $3",
    displayName: "Australia",
  },
  // Japan
  81: {
    pattern: /^(\d{2})(\d{4})(\d{4})$/,
    format: "$1-$2-$3",
    displayName: "Japan",
  },
  // China
  86: {
    pattern: /^(\d{3})(\d{4})(\d{4})$/,
    format: "$1 $2 $3",
    displayName: "China",
  },
  // India
  91: {
    pattern: /^(\d{5})(\d{5})$/,
    format: "$1 $2",
    displayName: "India",
  },
  // Brazil
  55: {
    pattern: /^(\d{2})(\d{4,5})(\d{4})$/,
    format: "($1) $2-$3",
    displayName: "Brazil",
  },
  // Mexico
  52: {
    pattern: /^(\d{3})(\d{3})(\d{4})$/,
    format: "$1 $2 $3",
    displayName: "Mexico",
  },
};

/**
 * Extract country code from E.164 phone number
 * @param {string} e164Phone - Phone number in E.164 format (e.g., +15551234567)
 * @returns {Object} - { countryCode: string, nationalNumber: string, isValid: boolean }
 */
const parseE164Phone = (e164Phone) => {
  if (!e164Phone || typeof e164Phone !== "string") {
    return { countryCode: null, nationalNumber: null, isValid: false };
  }

  const cleaned = e164Phone.trim();

  // Must start with + and contain only digits after
  if (!cleaned.startsWith("+") || !cleaned.substring(1).match(/^\d+$/)) {
    return { countryCode: null, nationalNumber: null, isValid: false };
  }

  const digits = cleaned.substring(1); // Remove the +

  // Try to match country codes (longest first to handle overlapping codes)
  const sortedCodes = Object.keys(COUNTRY_FORMATS).sort(
    (a, b) => b.length - a.length
  );

  for (const code of sortedCodes) {
    if (digits.startsWith(code)) {
      const nationalNumber = digits.substring(code.length);
      return {
        countryCode: code,
        nationalNumber: nationalNumber,
        isValid: true,
      };
    }
  }

  // If no known country code found, assume it's still valid E.164
  // Extract first 1-3 digits as country code
  const countryCode = digits.substring(0, Math.min(3, digits.length));
  const nationalNumber = digits.substring(countryCode.length);

  return {
    countryCode: countryCode,
    nationalNumber: nationalNumber,
    isValid: true,
  };
};

/**
 * Format E.164 phone number for display
 * @param {string} e164Phone - Phone number in E.164 format
 * @param {Object} options - Formatting options
 * @param {boolean} options.includeCountryCode - Whether to include country code in display
 * @param {boolean} options.international - Whether to show international format
 * @returns {string} - Formatted phone number for display
 */
export const formatPhoneForDisplay = (e164Phone, options = {}) => {
  const { includeCountryCode = true, international = false } = options;

  if (!e164Phone) {
    return "";
  }

  const parsed = parseE164Phone(e164Phone);
  if (!parsed.isValid) {
    return e164Phone; // Return as-is if not valid E.164
  }

  const { countryCode, nationalNumber } = parsed;
  const formatConfig = COUNTRY_FORMATS[countryCode];

  if (formatConfig && formatConfig.pattern.test(nationalNumber)) {
    // Apply country-specific formatting
    const formatted = nationalNumber.replace(
      formatConfig.pattern,
      formatConfig.format
    );

    if (international || (includeCountryCode && countryCode !== "1")) {
      return `+${countryCode} ${formatted}`;
    } else if (includeCountryCode && countryCode === "1") {
      // For NANP, just show formatted number without country code for domestic display
      return formatted;
    } else {
      return formatted;
    }
  } else {
    // Fallback formatting for unknown patterns
    if (includeCountryCode) {
      return `+${countryCode} ${nationalNumber}`;
    } else {
      return nationalNumber;
    }
  }
};

/**
 * Format phone number for domestic display (hide country code for local numbers)
 * @param {string} e164Phone - Phone number in E.164 format
 * @param {string} userCountry - User's country code (e.g., 'US', 'UK')
 * @returns {string} - Formatted phone number for domestic display
 */
export const formatPhoneForDomesticDisplay = (
  e164Phone,
  userCountry = "US"
) => {
  const parsed = parseE164Phone(e164Phone);
  if (!parsed.isValid) {
    return e164Phone;
  }

  const countryCodeMap = {
    US: "1",
    CA: "1",
    UK: "44",
    GB: "44",
    FR: "33",
    DE: "49",
    IT: "39",
    ES: "34",
    AU: "61",
    JP: "81",
    CN: "86",
    IN: "91",
    BR: "55",
    MX: "52",
  };

  const userCountryCode = countryCodeMap[userCountry.toUpperCase()];
  const phoneCountryCode = parsed.countryCode;

  // If phone is from same country as user, hide country code
  if (userCountryCode === phoneCountryCode) {
    return formatPhoneForDisplay(e164Phone, { includeCountryCode: false });
  } else {
    return formatPhoneForDisplay(e164Phone, { international: true });
  }
};

/**
 * Get country info from E.164 phone number
 * @param {string} e164Phone - Phone number in E.164 format
 * @returns {Object} - { countryCode: string, displayName: string, isRecognized: boolean }
 */
export const getCountryFromPhone = (e164Phone) => {
  const parsed = parseE164Phone(e164Phone);
  if (!parsed.isValid) {
    return { countryCode: null, displayName: null, isRecognized: false };
  }

  const formatConfig = COUNTRY_FORMATS[parsed.countryCode];
  if (formatConfig) {
    return {
      countryCode: parsed.countryCode,
      displayName: formatConfig.displayName,
      isRecognized: true,
    };
  } else {
    return {
      countryCode: parsed.countryCode,
      displayName: `Country +${parsed.countryCode}`,
      isRecognized: false,
    };
  }
};

/**
 * Validate E.164 format (frontend validation)
 * @param {string} phone - Phone number to validate
 * @returns {Object} - { isValid: boolean, error: string }
 */
export const validateE164Format = (phone) => {
  if (!phone || typeof phone !== "string") {
    return { isValid: false, error: "Phone number is required" };
  }

  const cleaned = phone.trim();

  if (!cleaned.startsWith("+")) {
    return { isValid: false, error: "Phone number must start with +" };
  }

  if (cleaned.length < 8 || cleaned.length > 16) {
    return {
      isValid: false,
      error: "Phone number must be 8-16 characters long",
    };
  }

  const digits = cleaned.substring(1);
  if (!digits.match(/^\d+$/)) {
    return {
      isValid: false,
      error: "Phone number must contain only digits after +",
    };
  }

  return { isValid: true, error: null };
};

/**
 * Format input phone number as user types (for input fields)
 * @param {string} input - Current input value
 * @param {string} countryCode - Selected country code
 * @returns {string} - Formatted input value
 */
export const formatPhoneInput = (input, countryCode = "1") => {
  if (!input) return "";

  // Remove all non-digit characters except +
  let cleaned = input.replace(/[^\d+]/g, "");

  // If starts with +, keep it
  if (cleaned.startsWith("+")) {
    return cleaned;
  }

  // Otherwise, apply country-specific formatting
  const formatConfig = COUNTRY_FORMATS[countryCode];
  if (formatConfig && formatConfig.pattern) {
    const digits = cleaned.replace(/\D/g, "");
    if (formatConfig.pattern.test(digits)) {
      return digits.replace(formatConfig.pattern, formatConfig.format);
    }
  }

  return cleaned;
};

/**
 * Get supported countries list
 * @returns {Array} - Array of { code: string, name: string, dialCode: string }
 */
export const getSupportedCountries = () => {
  return Object.entries(COUNTRY_FORMATS).map(([dialCode, config]) => ({
    dialCode: dialCode,
    name: config.displayName,
    code: getCountryCodeFromDialCode(dialCode),
  }));
};

/**
 * Helper to get ISO country code from dial code
 */
const getCountryCodeFromDialCode = (dialCode) => {
  const mapping = {
    1: "US",
    44: "GB",
    33: "FR",
    49: "DE",
    39: "IT",
    34: "ES",
    61: "AU",
    81: "JP",
    86: "CN",
    91: "IN",
    55: "BR",
    52: "MX",
  };
  return mapping[dialCode] || dialCode;
};

// Default export
export default {
  formatPhoneForDisplay,
  formatPhoneForDomesticDisplay,
  getCountryFromPhone,
  validateE164Format,
  formatPhoneInput,
  getSupportedCountries,
};
